import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';
import {
    ActivationStatus,
    CollectionPolicy,
} from '../components/pages/billing/collection/collectionModel';
import { CollectionAgreement } from '../components/pages/billing/collection/collectionAgreementModel';
import { buildAgreementRequest } from './collectionAgreementRequests';
import { buildPolicyRequest } from './collectionRequests';

const API = appConfig.apis.billing;
const baseUrl = API.address;

export interface FieldError {
    fieldName: string;
    message: string;
}

export class CollectionApiError extends Error {
    constructor(
        public status: number,
        message: string,
        public fieldErrors: FieldError[] = [],
        public data?: unknown,
    ) {
        super(message);
    }
}

export interface Meta {
    page?: number;
    size?: number;
    totalElements?: number;
    totalPages?: number;
    numberOfElements?: number;
    apiVersion?: string;
    apiName?: string;
    environment?: string;
}

export interface Paged<T> {
    data: T[];
    meta: Meta;
}

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) {
        if (res.status === 204) return null;
        const text = await res.text();
        return text ? JSON.parse(text) : null;
    }
    const data = await res.json().catch(() => null);
    const fieldErrors: FieldError[] = Array.isArray(data?.errors) ? data.errors : [];
    throw new CollectionApiError(res.status, data?.message || `API error: ${res.status}`, fieldErrors, data);
};

const get = async (path: string) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { headers: await getHeaders() }));

const post = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'POST', headers: await getHeaders(), body: JSON.stringify(data) }));

const put = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'PUT', headers: await getHeaders(), body: JSON.stringify(data) }));

const patch = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'PATCH', headers: await getHeaders(), body: JSON.stringify(data) }));

const qs = (params: Record<string, unknown>): string => {
    const s = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') s.set(k, String(v)); });
    const str = s.toString();
    return str ? `?${str}` : '';
};

const collectionApi = {
    listPolicies: (params: { status?: ActivationStatus | ''; page?: number; size?: number } = {}): Promise<Paged<CollectionPolicy>> =>
        get(`/collection-policies${qs({ status: params.status, page: params.page, size: params.size })}`),

    getPolicy: (id: string): Promise<{ data: CollectionPolicy; meta: Meta }> => get(`/collection-policies/${id}`),

    savePolicy: (policy: CollectionPolicy): Promise<{ data: CollectionPolicy; meta: Meta }> =>
        (policy.id
            ? put(`/collection-policies/${policy.id}`, buildPolicyRequest(policy))
            : post(`/collection-policies`, buildPolicyRequest(policy))),

    changePolicyStatus: (id: string, status: ActivationStatus, reason: string | null): Promise<{ data: CollectionPolicy; meta: Meta }> =>
        patch(`/collection-policies/${id}/status`, { status, reason }),

    listAgreements: (params: { search?: string; page?: number; size?: number } = {}): Promise<Paged<CollectionAgreement>> =>
        get(`/collection-agreements${qs({ search: params.search, page: params.page, size: params.size })}`),

    getAgreement: (id: string): Promise<{ data: CollectionAgreement; meta: Meta }> => get(`/collection-agreements/${id}`),

    saveAgreement: (agreement: CollectionAgreement): Promise<{ data: CollectionAgreement; meta: Meta }> =>
        (agreement.id
            ? put(`/collection-agreements/${agreement.id}`, buildAgreementRequest(agreement))
            : post(`/collection-agreements`, buildAgreementRequest(agreement))),
};

export default collectionApi;
